import type { IApiKeyQueryParams, IApiKeyRepository } from '@yawi/domain';
import type {
    ApiKey,
    ApiKeyCreatedResponse,
    ApiKeyUsageStats,
    CreateApiKeyDto,
    ListApiKeysResponse,
    RevokeApiKeyDto,
    UpdateApiKeyDto,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiApiKeyRepository implements IApiKeyRepository {
  private readonly basePath = 'v1/api-keys';

  async findAll(params?: IApiKeyQueryParams): Promise<ListApiKeysResponse> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params?.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await client.get<ListApiKeysResponse>(url);
    return response;
  }

  async findById(id: string): Promise<ApiKey> {
    const client = getApiClient();
    const response = await client.get<ApiKey>(`${this.basePath}/${id}`);
    return response;
  }

  async create(dto: CreateApiKeyDto): Promise<ApiKeyCreatedResponse> {
    const client = getApiClient();
    const response = await client.post<ApiKeyCreatedResponse>(this.basePath, dto);
    return response;
  }

  async update(id: string, dto: UpdateApiKeyDto): Promise<ApiKey> {
    const client = getApiClient();
    const response = await client.patch<ApiKey>(`${this.basePath}/${id}`, dto);
    return response;
  }

  async delete(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/${id}`);
  }

  async revoke(id: string, dto?: RevokeApiKeyDto): Promise<ApiKey> {
    const client = getApiClient();
    const response = await client.post<ApiKey>(`${this.basePath}/${id}/revoke`, dto || {});
    return response;
  }

  async regenerate(id: string): Promise<ApiKeyCreatedResponse> {
    const client = getApiClient();
    const response = await client.post<ApiKeyCreatedResponse>(`${this.basePath}/${id}/regenerate`);
    return response;
  }

  async getStats(id: string): Promise<ApiKeyUsageStats> {
    const client = getApiClient();
    const response = await client.get<ApiKeyUsageStats>(`${this.basePath}/${id}/stats`);
    return response;
  }
}
